/**
 * SCM 库存比对详情抽屉
 * 参考 UCM 详情抽屉的设计风格：宽抽屉、顶部 Alert 横幅、Descriptions 头部、
 * 分 Tab 展示比对明细 + 损坏清单 + 类型汇总 + 原文，表格列带损坏标记。
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Drawer, Tag, Table, Tabs, Spin, Button, Space,
    Popconfirm, message, Typography, Alert, Segmented, Tooltip,
} from 'antd';
import {
    CheckCircleOutlined, StopOutlined, CopyOutlined,
    CheckOutlined, ExclamationCircleOutlined, SyncOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { scmService } from '../../../../api/enhancedApi';
import PermissionGuard from '../../../../components/auth/PermissionGuard';

const { Text } = Typography;

const STATUS_MAP = {
    pending: { label: '待处理', color: 'processing' },
    confirmed: { label: '已确认', color: 'success' },
    ignored: { label: '已忽略', color: 'default' },
    parse_failed: { label: '解析失败', color: 'error' },
};

const STATION_MATCH_MAP = {
    matched: { label: '已匹配', color: 'success' },
    unknown: { label: '站点未知', color: 'warning' },
};

const tag = (map, v) => {
    const item = map[v] || {};
    return <Tag color={item.color}>{item.label || v || '-'}</Tag>;
};

const buildComparisonRows = (items, groups, category, damageMap) => {
    if (!items?.comparison) return [];
    const ids = items.comparison[category] || [];
    const allGroupItems = (groups || []).flatMap((g) =>
        (g.items || []).map((item) => ({ ...item, uld_type: g.uld_type || item.uld_type }))
    );

    return ids.map((id, idx) => {
        const found = allGroupItems.find((gi) => gi.full_id === id || gi.identifier_autofixed_from === id);
        const damage = damageMap[id] || (found ? damageMap[found.full_id] : null) || null;
        if (found) return { key: `${category}-${idx}`, ...found, damage };
        return {
            key: `${category}-${idx}`,
            full_id: id,
            uld_type: id?.substring(0, 3) || '-',
            serial: '',
            owner: '',
            damage,
        };
    });
};

const comparisonColumns = [
    {
        title: 'ULD 标识',
        dataIndex: 'full_id',
        key: 'full_id',
        width: 200,
        fixed: 'left',
        render: (v, record) => (
            <Space size={4} direction="vertical" style={{ width: '100%' }}>
                <Space size={4}>
                    <Text copyable={{ text: v }} style={{ fontFamily: 'Courier New, monospace', fontSize: 13 }}>{v}</Text>
                    {record.damage && <Tag color="orange" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>损</Tag>}
                </Space>
                {record.identifier_autofixed_from && (
                    <Tooltip title={`报文原文为 ${record.identifier_autofixed_from}，所属人代码缺尾字母，已按台账兜底补全为 ${v}`}>
                        <Tag color="blue" style={{ margin: 0 }}>补全自 {record.identifier_autofixed_from}</Tag>
                    </Tooltip>
                )}
            </Space>
        ),
    },
    { title: '类型', dataIndex: 'uld_type', key: 'uld_type', width: 65 },
    { title: '序列号', dataIndex: 'serial', key: 'serial', width: 80 },
    { title: '归属方', dataIndex: 'owner', key: 'owner', width: 70 },
    {
        title: '损坏描述',
        dataIndex: 'damage',
        key: 'damage',
        ellipsis: true,
        render: (v) => v ? <Text type="warning" style={{ fontSize: 12 }}>{v}</Text> : <Text type="secondary">-</Text>,
    },
];

const damageColumns = [
    {
        title: 'ULD 标识',
        dataIndex: 'full_id',
        key: 'full_id',
        width: 150,
        render: (v) => <Text style={{ fontFamily: 'Courier New, monospace', fontSize: 13 }}>{v}</Text>,
    },
    { title: '类型', dataIndex: 'uld_type', key: 'uld_type', width: 65 },
    { title: '序列号', dataIndex: 'serial', key: 'serial', width: 80 },
    { title: '归属方', dataIndex: 'owner', key: 'owner', width: 70 },
    {
        title: '损坏描述',
        dataIndex: 'description',
        key: 'description',
        render: (v) => <Text type="warning">{v || '-'}</Text>,
    },
];

const typeGroupColumns = [
    { title: '类型代码', dataIndex: 'uld_type', key: 'uld_type', width: 100 },
    { title: '数量', dataIndex: 'count', key: 'count', width: 80, render: (v) => <Text strong>{v}</Text> },
    {
        title: '损坏',
        dataIndex: 'damaged',
        key: 'damaged',
        width: 80,
        render: (v) => v > 0 ? <Tag color="orange">{v}</Tag> : <Text type="secondary">0</Text>,
    },
];

const copyText = (text, successMsg) => {
    if (!text) {
        message.warning('没有可复制的内容');
        return;
    }
    navigator.clipboard.writeText(text).then(
        () => message.success(successMsg),
        () => message.error('复制失败')
    );
};

const ScmInboundDetailDrawer = ({ visible, pendingId, onClose, onChanged }) => {
    const [loading, setLoading] = useState(false);
    const [reparsing, setReparsing] = useState(false);
    const [detail, setDetail] = useState(null);
    const [activeTab, setActiveTab] = useState('detail');

    useEffect(() => {
        if (visible && pendingId) {
            loadDetail(pendingId);
        }
        if (!visible) {
            setDetail(null);
            setActiveTab('detail');
        }
    }, [visible, pendingId]);

    const loadDetail = async (id) => {
        setLoading(true);
        try {
            const resp = await scmService.getScmPendingById(id);
            setDetail(resp.data);
        } catch (error) {
            message.error('加载详情失败');
        } finally {
            setLoading(false);
        }
    };

    const handleIgnore = async () => {
        try {
            const resp = await scmService.ignoreScmPending(pendingId);
            message.success(resp.data?.message || '已忽略');
            onChanged && onChanged();
            loadDetail(pendingId);
        } catch (error) {
            message.error(error.response?.data?.detail || '忽略失败');
        }
    };

    const handleReparse = async () => {
        setReparsing(true);
        try {
            const resp = await scmService.reparseScmPending(pendingId);
            message.success(resp.data?.message || '已重新解析并刷新库存比对');
            await loadDetail(pendingId);
            onChanged && onChanged();
        } catch (error) {
            message.error(error.response?.data?.detail || '重新解析失败');
        } finally {
            setReparsing(false);
        }
    };

    const handleCopyRaw = () => {
        if (!detail?.raw_message) return;
        copyText(detail.raw_message, '已复制原始报文');
    };

    const items = detail?.items || {};
    const groups = items?.uld_groups || [];
    const damageNotes = items?.damage_notes || [];
    const autofixed = items?.comparison?.autofixed || [];

    const damageMap = useMemo(() => {
        const m = {};
        damageNotes.forEach((n) => { m[n.full_id] = n.description; });
        return m;
    }, [damageNotes]);

    const matchedRows = buildComparisonRows(items, groups, 'matched', damageMap);
    const scmOnlyRows = buildComparisonRows(items, groups, 'scm_only', damageMap);
    const ledgerOnlyRows = buildComparisonRows(items, groups, 'ledger_only', damageMap);
    const damageRows = damageNotes.map((n, idx) => ({ key: `dmg-${idx}`, ...n }));

    const typeGroupRows = groups.map((g, idx) => {
        const dmgCount = (g.items || []).filter((i) => damageMap[i.full_id]).length;
        return { key: `grp-${idx}`, uld_type: g.uld_type, count: g.count || (g.items || []).length, damaged: dmgCount };
    });

    const ignorable = detail?.status === 'pending' || detail?.status === 'parse_failed';
    const reparsable = detail?.status === 'pending' || detail?.status === 'parse_failed';

    const handleCopyDiffUlds = useCallback(() => {
        const scmOnly = (items?.comparison?.scm_only || []).filter(Boolean);
        const ledgerOnly = (items?.comparison?.ledger_only || []).filter(Boolean);
        if (scmOnly.length === 0 && ledgerOnly.length === 0) {
            message.warning('当前没有差异 ULD');
            return;
        }
        const parts = [];
        if (scmOnly.length > 0) {
            parts.push(`# SCM有/台账无 (${scmOnly.length})`);
            parts.push(...scmOnly);
        }
        if (ledgerOnly.length > 0) {
            if (parts.length > 0) parts.push('');
            parts.push(`# 台账有/SCM无 (${ledgerOnly.length})`);
            parts.push(...ledgerOnly);
        }
        copyText(parts.join('\n'), `已复制差异 ULD（${scmOnly.length + ledgerOnly.length} 块）`);
    }, [items]);

    const handleCopyCurrentCategory = useCallback((rows, label) => {
        const ids = (rows || []).map((r) => r.full_id).filter(Boolean);
        copyText(ids.join('\n'), `已复制${label}（${ids.length} 块）`);
    }, []);

    const alertBanner = useMemo(() => {
        if (!detail) return null;
        if (detail.status === 'confirmed') {
            return (
                <Alert
                    type="success" showIcon icon={<CheckCircleOutlined />}
                    message={`已确认 · ${detail.confirmed_by || ''}${detail.confirmed_at ? ' · ' + dayjs(detail.confirmed_at).format('YYYY-MM-DD HH:mm') : ''}`}
                    style={{ marginBottom: 16 }}
                />
            );
        }
        if (detail.status === 'ignored') {
            return (
                <Alert
                    type="info" showIcon icon={<StopOutlined />}
                    message={`已忽略 · ${detail.confirmed_by || ''}${detail.confirmed_at ? ' · ' + dayjs(detail.confirmed_at).format('YYYY-MM-DD HH:mm') : ''}`}
                    style={{ marginBottom: 16 }}
                />
            );
        }
        if (detail.status === 'parse_failed') {
            return <Alert type="error" showIcon message="解析失败，请核对原文" style={{ marginBottom: 16 }} />;
        }
        const hasIssues = detail.scm_only_count > 0 || detail.ledger_only_count > 0;
        if (hasIssues) {
            return (
                <Alert
                    type="warning" showIcon icon={<ExclamationCircleOutlined />}
                    message={`比对存在差异：SCM有/台账无 ${detail.scm_only_count} 块，台账有/SCM无 ${detail.ledger_only_count} 块`}
                    action={
                        <Button size="small" type="link" icon={<CopyOutlined />} onClick={handleCopyDiffUlds}>
                            复制差异 ULD
                        </Button>
                    }
                    style={{ marginBottom: 16 }}
                />
            );
        }
        return (
            <Alert
                type="success" showIcon icon={<CheckOutlined />}
                message={`比对无差异：全部 ${detail.uld_total} 块匹配${autofixed.length > 0 ? `（含 ${autofixed.length} 块 UT→UTA 兜底补全）` : ''}`}
                style={{ marginBottom: 16 }}
            />
        );
    }, [detail, handleCopyDiffUlds, autofixed.length]);

    const allItemRows = useMemo(() => {
        return (groups || []).flatMap((g) =>
            (g.items || []).map((item, idx) => ({
                key: `all-${g.uld_type}-${idx}`,
                ...item,
                uld_type: g.uld_type || item.uld_type,
                damage: damageMap[item.full_id] || damageMap[item.identifier_autofixed_from] || null,
            }))
        );
    }, [groups, damageMap]);

    const [category, setCategory] = useState('scm_only');

    useEffect(() => {
        if (!detail) return;
        if (detail.scm_only_count > 0) setCategory('scm_only');
        else if (detail.ledger_only_count > 0) setCategory('ledger_only');
        else setCategory('all');
    }, [detail?.id]);

    const categoryOptions = [
        { value: 'scm_only', label: `SCM有/台账无 (${scmOnlyRows.length})` },
        { value: 'ledger_only', label: `台账有/SCM无 (${ledgerOnlyRows.length})` },
        { value: 'matched', label: `匹配 (${matchedRows.length})` },
        ...(damageRows.length > 0 ? [{ value: 'damaged', label: `损坏 (${damageRows.length})` }] : []),
        { value: 'all', label: `全部 (${detail?.uld_total || 0})` },
    ];

    const categoryDataMap = {
        all: allItemRows,
        matched: matchedRows,
        scm_only: scmOnlyRows,
        ledger_only: ledgerOnlyRows,
        damaged: damageRows,
    };

    const categoryCopyLabel = {
        scm_only: 'SCM有/台账无',
        ledger_only: '台账有/SCM无',
        matched: '匹配清单',
        damaged: '损坏清单',
        all: '全部清单',
    };

    const paginationConfig = { defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['50', '100', '200'], showTotal: (t) => `共 ${t} 条` };

    const tabItems = [
        {
            key: 'detail',
            label: '比对明细',
            children: (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                        <Segmented
                            size="small"
                            value={category}
                            onChange={setCategory}
                            options={categoryOptions}
                        />
                        <Space size={4}>
                            {(detail?.scm_only_count > 0 || detail?.ledger_only_count > 0) && (
                                <Button size="small" icon={<CopyOutlined />} onClick={handleCopyDiffUlds}>
                                    复制差异 ULD
                                </Button>
                            )}
                            <Button
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={() => handleCopyCurrentCategory(categoryDataMap[category], categoryCopyLabel[category] || '当前列表')}
                            >
                                复制当前列表
                            </Button>
                        </Space>
                    </div>
                    <Table
                        key={category}
                        columns={category === 'damaged' ? damageColumns : comparisonColumns}
                        dataSource={categoryDataMap[category] || []}
                        size="small"
                        pagination={paginationConfig}
                        scroll={{ x: 600 }}
                    />
                </div>
            ),
        },
        ...(typeGroupRows.length > 1 ? [{
            key: 'groups',
            label: `类型汇总 (${typeGroupRows.length})`,
            children: (
                <Table
                    columns={typeGroupColumns}
                    dataSource={typeGroupRows}
                    size="small"
                    pagination={false}
                />
            ),
        }] : []),
        {
            key: 'raw',
            label: '原始报文',
            children: (
                <div>
                    <div style={{ marginBottom: 8, textAlign: 'right' }}>
                        <Button size="small" icon={<CopyOutlined />} onClick={handleCopyRaw}>复制原文</Button>
                    </div>
                    <pre style={{
                        maxHeight: 600, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                        fontSize: 12, fontFamily: 'Courier New, monospace',
                        background: '#fafafa', padding: 16, borderRadius: 6, border: '1px solid #f0f0f0',
                    }}>
                        {detail?.raw_message || '无'}
                    </pre>
                </div>
            ),
        },
    ];

    return (
        <Drawer
            title={
                <Space>
                    <span>{`SCM 库存比对详情 #${pendingId || ''}`}</span>
                    {detail && tag(STATUS_MAP, detail.status)}
                </Space>
            }
            open={visible}
            onClose={onClose}
            width={960}
            footer={
                detail && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {detail.created_at ? `解析于 ${dayjs(detail.created_at).format('YYYY-MM-DD HH:mm:ss')}` : ''}
                        </Text>
                        <PermissionGuard permission="uld:scm_inbound:confirm">
                            <Space>
                                <Popconfirm
                                    title="重新解析当前记录？"
                                    description="将使用原始报文和当前台账刷新解析与库存比对结果，不会新建记录。"
                                    okText="重新解析"
                                    cancelText="取消"
                                    onConfirm={handleReparse}
                                    disabled={!reparsable}
                                >
                                    <Button icon={<SyncOutlined />} loading={reparsing} disabled={!reparsable}>
                                        重新解析
                                    </Button>
                                </Popconfirm>
                                <Popconfirm title="忽略该记录？" okText="忽略" cancelText="取消" onConfirm={handleIgnore} disabled={!ignorable}>
                                    <Button danger icon={<StopOutlined />} disabled={!ignorable || reparsing}>
                                        忽略
                                    </Button>
                                </Popconfirm>
                            </Space>
                        </PermissionGuard>
                    </div>
                )
            }
        >
            <Spin spinning={loading}>
                {detail && (
                    <>
                        {alertBanner}

                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px 16px', marginBottom: 16, padding: '8px 12px', background: '#fafafa', borderRadius: 6 }}>
                            <Space size={4}>
                                <Text type="secondary">SCM 识别</Text>
                                <Text strong>{detail.uld_total} 块</Text>
                            </Space>
                            <Space size={4}>
                                <Text type="secondary">台账在库</Text>
                                <Text strong>{detail.matched_count + detail.ledger_only_count} 块</Text>
                            </Space>
                            <span style={{ color: '#d9d9d9' }}>|</span>
                            <Space size={4}>
                                <Text type="secondary">站点</Text>
                                <Text strong>{detail.station || '-'}</Text>
                                {tag(STATION_MATCH_MAP, detail.station_match_status)}
                            </Space>
                            {autofixed.length > 0 && (
                                <>
                                    <span style={{ color: '#d9d9d9' }}>|</span>
                                    <Space size={4}>
                                        <Text type="secondary">板号兜底</Text>
                                        <Tag color="blue" style={{ margin: 0 }}>UT→UTA {autofixed.length}</Tag>
                                    </Space>
                                </>
                            )}
                            <span style={{ color: '#d9d9d9' }}>|</span>
                            <Space size={4}>
                                <Text type="secondary">报文时间</Text>
                                <Text>{detail.report_time ? dayjs(detail.report_time).format('YYYY-MM-DD HH:mm') : '-'}</Text>
                            </Space>
                            <span style={{ color: '#d9d9d9' }}>|</span>
                            <Space size={4}>
                                <Text type="secondary">来源</Text>
                                <Tag color={detail.source === 'email' ? 'blue' : 'green'} style={{ margin: 0 }}>
                                    {detail.source === 'email' ? '邮件' : '人工'}
                                </Tag>
                            </Space>
                        </div>

                        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
                    </>
                )}
            </Spin>
        </Drawer>
    );
};

export default ScmInboundDetailDrawer;
